import { Button } from 'primereact/button';
import { type MouseEvent, type ReactNode } from 'react';

import { cn } from '@shared/lib/cn';

/** `link` is an inline text action (breadcrumb, "Forgot PIN?", a clickable figure): no box, inherits the text. */
export type BaseButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type BaseButtonSize = 'sm' | 'md';

export interface BaseButtonProps {
  children?: ReactNode;
  /** PrimeIcons class, e.g. `pi pi-plus`. */
  icon?: string;
  variant?: BaseButtonVariant;
  size?: BaseButtonSize;
  type?: 'button' | 'submit' | 'reset';
  loading?: boolean;
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  'aria-label'?: string;
  'aria-haspopup'?: boolean;
  title?: string;
  /** Focus on mount (the default action of a drawer, so Enter doesn't hit × close). */
  autoFocus?: boolean;
}

const VARIANT_CLASS: Record<BaseButtonVariant, string | undefined> = {
  primary: undefined,
  secondary: 'p-button-outlined',
  ghost: 'p-button-text',
  danger: 'p-button-danger',
  link: 'p-button-link',
};

/** The only button in the app. Wraps PrimeReact `Button` with the Compliance Desk look. */
export function BaseButton({
  children,
  icon,
  variant = 'primary',
  size = 'md',
  type = 'button',
  loading = false,
  disabled = false,
  onClick,
  className,
  title,
  autoFocus,
  'aria-label': ariaLabel,
  'aria-haspopup': ariaHasPopup,
}: BaseButtonProps) {
  return (
    <Button
      type={type}
      icon={icon}
      loading={loading}
      disabled={disabled || loading}
      onClick={onClick}
      title={title}
      autoFocus={autoFocus}
      aria-label={ariaLabel}
      aria-haspopup={ariaHasPopup}
      className={cn(
        VARIANT_CLASS[variant],
        size === 'sm' && variant !== 'link' && 'p-button-sm',
        variant !== 'link' && !!children && 'gap-1.5',
        !children && !!icon && 'p-button-icon-only',
        className,
      )}
    >
      {children}
    </Button>
  );
}
